/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { ReactNode } from 'react'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { useTranslation } from 'react-i18next'
import {useDialogFocus} from 'utils/hooks/useDialogFocus'

interface EditCompanyModalInlineOverlayProps {
    isOpen: boolean
    onClose: () => void
    children: ReactNode
    title?: string
}

const EditCompanyModalInlineOverlay = ({
    isOpen,
    onClose,
    children,
    title,
}: EditCompanyModalInlineOverlayProps) => {
    const { t } = useTranslation()
    const dialogRef = useDialogFocus(isOpen, true, onClose)

    if (!isOpen) {
        return null
    }

    return (
        <div className="EditCompanyModalInlineOverlay fixed inset-0 z-[1400] flex items-center justify-center bg-black/30 dark:bg-black/50 px-4">
            <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={title || t('Dialog')} tabIndex={-1} className="EditCompanyModalInlineOverlayContent bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-auto relative">
                <button
                    onClick={onClose}
                    type="button"
                    className="EditCompanyModalInlineOverlayClose absolute right-4 top-4 size-11 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white transition-colors rounded-md z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cta-light focus-visible:ring-offset-2"
                >
                    <span className="sr-only">{t('Close')}</span>
                    <XMarkIcon className="w-5 h-5" aria-hidden="true" />
                </button>
                <div className="EditCompanyModalInlineOverlayBody p-4">{children}</div>
            </div>
        </div>
    )
}

export default EditCompanyModalInlineOverlay
